import React from "react";
import { useNavigate } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { posthog } from "@/lib/posthog";
import { getCookieConsent, hasDecidedCookieConsent, setCookieConsent } from "@/lib/cookieConsent";

/**
 * Shows once, on first visit (before any consent decision exists), for every
 * visitor — authenticated or not. The decision is applied to PostHog
 * immediately and persisted in localStorage (see cookieConsent.ts for why
 * this doesn't route through user_preferences).
 */
export default function CookieConsentBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(false);
  const [managing, setManaging] = React.useState(false);
  const [analyticsChoice, setAnalyticsChoice] = React.useState(false);
  const [replayChoice, setReplayChoice] = React.useState(false);

  React.useEffect(() => {
    const sync = () => setVisible(!hasDecidedCookieConsent());
    sync();
    window.addEventListener('novari:cookie-consent', sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener('novari:cookie-consent', sync); window.removeEventListener('storage', sync); };
  }, []);

  const applyDecision = (analytics: boolean, replay = false) => {
    setCookieConsent(analytics, replay);
    if (analytics) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
    setVisible(false);
    setManaging(false);
  };

  const openManage = () => {
    setAnalyticsChoice(getCookieConsent()?.analytics ?? false);
    setReplayChoice(getCookieConsent()?.replay ?? false);
    setManaging(true);
  };

  if (!visible && !managing) return null;

  return (
    <>
      {/* Hidden while the Manage Preferences dialog is open — the banner's
          z-index sits above the Dialog overlay (by design, so it isn't
          dismissible by clicking outside it like a modal), which would
          otherwise punch through the dialog's dim backdrop. */}
      {visible && !managing && (
        <div
          role="region"
          aria-label="Cookie consent"
          className="fixed inset-x-0 z-toast bottom-[calc(52px+env(safe-area-inset-bottom)+0.5rem)] px-4 lg:bottom-4 lg:right-auto lg:px-0 lg:left-4"
        >
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-sm rounded-[var(--radius-xl,1.25rem)] border border-line bg-[rgba(var(--bg-surface-rgb),0.95)] backdrop-blur-2xl shadow-2xl p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-tok-accent-primary/10 flex items-center justify-center shrink-0">
                <Cookie className="w-4.5 h-4.5 text-[var(--accent-primary)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--text-primary)]">
                  We use cookies
                </p>
                <p className="text-[13px] text-[var(--text-secondary)] mt-1 leading-relaxed">
                  Essential cookies keep you signed in and remember your preferences. With your
                  permission, we use usage analytics and masked session recordings to understand
                  what works and fix bugs. These can be linked to your tester account.
                  Your typed text and study content are masked, and the data is never sold. See our{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/privacy")}
                    className="underline underline-offset-2 hover:text-[var(--accent-primary)]"
                  >
                    Privacy Policy
                  </button>{" "}
                  for details.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 justify-end">
              <Button variant="ghost" size="sm" onClick={openManage}>
                Manage
              </Button>
              <Button variant="outline" size="sm" onClick={() => applyDecision(false)}>
                Decline
              </Button>
              <Button size="sm" style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 90%, #000)', color: '#fff' }} onClick={() => applyDecision(true, true)}>
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={managing} onOpenChange={setManaging}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose what Novari Study is allowed to use on this device.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4 rounded-xl border border-line p-3.5">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Necessary</p>
                <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
                  Required for login and core functionality. Always on.
                </p>
              </div>
              <Switch checked disabled aria-label="Necessary cookies (always on)" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-line p-3.5">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Analytics</p>
                <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
                  Usage data (features opened, session length) helps us fix bugs and
                  improve the app, never sold.
                </p>
              </div>
              <Switch
                checked={analyticsChoice}
                onCheckedChange={setAnalyticsChoice}
                aria-label="Analytics cookies"
              />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-xl border border-line p-3.5">
              <div>
                <p className="text-sm font-medium">Session recordings</p>
                <p className="text-[12px] text-ink-2 mt-0.5">For testers who agree: masked recordings of clicks, navigation and errors help us investigate problems. Requires analytics. Change this any time in Settings → Privacy.</p>
              </div>
              <Switch checked={analyticsChoice && replayChoice} onCheckedChange={setReplayChoice} disabled={!analyticsChoice} aria-label="Session recordings" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => applyDecision(false)}>
              Decline All
            </Button>
            <Button onClick={() => applyDecision(analyticsChoice, replayChoice)}>Save Preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
